import type { KeyboardEvent } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { formatDateTime, formatFileSize } from "../../utils/format";
import { colorTokens } from "../../theme/tokens";
import { useRowMenu } from "../../hooks/useRowMenu";
import type { ExplorerRow } from "./ExplorerTable";

export interface ExplorerGridProps {
  caption: string;
  rows: ExplorerRow[];
  loading: boolean;
  emptyMessage: string;
  /** Selection checkboxes for the Actions toolbar; omitted at the folder-list root view (same as ExplorerTable). */
  selectedKeys?: ReadonlySet<string>;
  onToggleSelect?: (key: string) => void;
}

/**
 * The explorer's card-grid view (toolbar View toggle): the same rows,
 * selection and per-row contextual menu (right-click, "more" button,
 * Shift+F10/ContextMenu, long-press) as ExplorerTable, via the shared
 * useRowMenu hook, so the two views cannot drift apart. Sorting has no
 * meaning as a layout in a grid (there is no column to sort by), so the
 * caller still sorts `rows` before handing them to either view; only the
 * table renders sortable headers.
 */
export function ExplorerGrid({
  caption,
  rows,
  loading,
  emptyMessage,
  selectedKeys,
  onToggleSelect,
}: ExplorerGridProps) {
  const rowMenu = useRowMenu();

  if (loading) {
    return (
      <Stack sx={{ alignItems: "center", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} aria-label={`Loading ${caption}`} />
      </Stack>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  function handleCardKeyDown(event: KeyboardEvent<HTMLDivElement>, row: ExplorerRow) {
    if ((event.key === "Enter" || event.key === " ") && row.onActivate) {
      event.preventDefault();
      row.onActivate();
      return;
    }
    rowMenu.handleKeyDown(event, row.key, row.actions);
  }

  const menuRow = rowMenu.menu ? rows.find((row) => row.key === rowMenu.menu!.rowKey) : undefined;

  return (
    <Box
      role="grid"
      aria-label={caption}
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: 2,
        p: 2,
      }}
    >
      {rows.map((row) => {
        const selected = selectedKeys?.has(row.key) ?? false;
        return (
          <Card
            key={row.key}
            variant="outlined"
            role="row"
            aria-selected={selectedKeys ? selected : undefined}
            tabIndex={row.actions?.length ? 0 : undefined}
            onContextMenu={(event) => rowMenu.handleContextMenu(event, row.key, row.actions)}
            onKeyDown={(event) => handleCardKeyDown(event, row)}
            onTouchStart={(event) => rowMenu.handleTouchStart(event, row.key, row.actions)}
            onTouchEnd={rowMenu.clearLongPress}
            onTouchMove={rowMenu.clearLongPress}
            onTouchCancel={rowMenu.clearLongPress}
            sx={{
              position: "relative",
              bgcolor: selected ? colorTokens.brandSubtle : colorTokens.surface,
              "&:focus-visible": { outline: `2px solid ${colorTokens.brand}`, outlineOffset: -2 },
            }}
          >
            {onToggleSelect && (
              <Checkbox
                size="small"
                checked={selected}
                onChange={() => onToggleSelect(row.key)}
                sx={{ position: "absolute", top: 2, left: 2 }}
                slotProps={{ input: { "aria-label": `Select ${row.name}` } }}
              />
            )}
            {Boolean(row.actions?.length) && (
              <IconButton
                aria-label={`More actions for ${row.name}`}
                size="small"
                sx={{ position: "absolute", top: 2, right: 2 }}
                onClick={(event) => rowMenu.openAtElement(row.key, event.currentTarget)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
            <CardContent sx={{ pt: 5, textAlign: "center" }}>
              <Stack spacing={1} sx={{ alignItems: "center" }}>
                {row.kind === "folder" ? (
                  <FolderIcon sx={{ fontSize: 40, color: colorTokens.textMuted }} />
                ) : (
                  <InsertDriveFileIcon sx={{ fontSize: 40, color: colorTokens.textMuted }} />
                )}
                <Tooltip title={row.name}>
                  <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
                    {row.onActivate ? (
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={row.onActivate}
                        sx={{
                          display: "block",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.name}
                      </Link>
                    ) : row.href ? (
                      <Link
                        href={row.href}
                        download
                        underline="hover"
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.name}
                      </Link>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {row.name}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
                {(row.lastModified !== null || row.size !== null) && (
                  <Typography variant="caption" color="text.secondary">
                    {row.lastModified ? formatDateTime(row.lastModified) : ""}
                    {row.lastModified && row.size !== null ? " · " : ""}
                    {row.size !== null ? formatFileSize(row.size) : ""}
                  </Typography>
                )}
                {row.extra}
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {rowMenu.menu && menuRow?.actions && (
        <Menu
          open
          onClose={rowMenu.close}
          anchorEl={rowMenu.menu.anchorEl ?? undefined}
          anchorReference={rowMenu.menu.anchorPosition ? "anchorPosition" : "anchorEl"}
          anchorPosition={rowMenu.menu.anchorPosition ?? undefined}
        >
          {menuRow.actions.map((action) => (
            <MenuItem
              key={action.key}
              onClick={() => {
                rowMenu.close();
                action.onSelect();
              }}
            >
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
}
